import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import SafeAreaInsets from 'safe-area-insets'

import { statusBarStyles, keyboardBehaviors } from '@adalo/constants'

import {
  StatusBar,
  Dimensions,
  Platform,
  ScrollView,
  StyleSheet,
  View,
  Keyboard,
} from 'react-native'

import { hasDataAction } from 'utils/action-filtering'
import { Alert } from 'utils/alerts'
import { getDependencies, getInputDependencies } from 'utils/dependencies'
import { actionContextTypes, executeAction } from 'utils/actions'
import { normalizeColor, defaultBranding } from 'utils/colors'
import { isMobileWeb } from 'utils/system'
import { filterObject } from 'utils/objects'
import { shallowEqual } from 'utils/hashing'
import { isListObject } from 'utils/lists'
import { isCloseToBottom } from 'utils/scrolling'
import { disableTrialFeature, removeExternalCollections } from 'utils/trial'

import { fetch } from 'ducks/data'
import { getFormInputs } from 'ducks/formInputs'

import ObjectRenderer from './ObjectRenderer'
import KeyboardAvoidingView from './KeyboardAvoidingView'
import ListBody from './ListBody'
import BackgroundImage from './BackgroundImage'

class Screen extends Component {
  static contextTypes = {
    ...actionContextTypes,
    getBaseURL: PropTypes.func,
    getAssetURL: PropTypes.func,
    getDatasources: PropTypes.func,
    getStore: PropTypes.func,
    getApp: PropTypes.func,
    appVersion: PropTypes.string,
  }

  static childContextTypes = {
    getParams: PropTypes.func,
    setDirty: PropTypes.func,
    getBinding: PropTypes.func,
    getBindings: PropTypes.func,
    getComponent: PropTypes.func,
    getBindingsList: PropTypes.func,
  }

  state = {
    updateVersion: 0,
    offset: 0,
    loading: false,
    loaded: false,
    refreshing: false,
    reachedPaginationEnd: false,
    initialRender: true,
  }

  pollTimers = []

  getChildContext() {
    const { component } = this.props
    return {
      getParams: this.getParams,
      setDirty: this.setDirty,
      getBinding: () => null,
      getBindings: () => ({}),
      getBindingsList: () => [],
      getComponent: () => component,
    }
  }

  getParams = () => {
    const { params } = this.props

    return params || {}
  }

  setDirty = () => {
    this.setState(({ updateVersion }) => ({
      updateVersion: updateVersion + 1,
    }))
  }

  getDependencies = () => {
    const { component, formInputs } = this.props
    const { getStore, getDatasources, getApp } = this.context
    const state = getStore().getState()

    const app = getApp()
    const disableExternalCollections = disableTrialFeature(app)

    let dependencies = []

    try {
      dependencies = getDependencies(component, {
        formInputs,
        getDatasources,
        getApp,
        state,
        getParams: this.getParams,
      })

      if (disableExternalCollections) {
        dependencies = removeExternalCollections(getDatasources, dependencies)
      }
    } catch (err) {
      Alert.alert(
        'Failed to load dependencies',
        'Try checking your authentication configuration and make sure your ' +
          'user is properly authenticated before reaching this page.',
        [{ text: 'OK', onPress: () => {} }]
      )
      console.log('ERROR FEETCHING DEPENDENCIES:', err)
    }

    return dependencies
  }

  handleRefresh = async (skipPolling = true) => {
    this.setState({
      offset: 0,
      refreshing: true,
      reachedPaginationEnd: false,
    })

    const dependencies = this.getDependencies()
    await this.fetchDependencies(dependencies, skipPolling)

    this.setState({ refreshing: false })
  }

  fetchDependencies = async (dependencies, skipPolling = false) => {
    // Figure out what data is needed and fetch it

    this.teardown()

    for (const dep of dependencies) {
      if (dep.fetchFirst) {
        // eslint-disable-next-line no-await-in-loop
        await this.fetchDependency(dep)
      }
    }

    const promises = dependencies.map(dep => {
      if (dep.listenForChanges && !skipPolling) {
        this.startPolling(dep)
      }

      return this.fetchDependency(dep)
    })

    return Promise.all(promises)
  }

  fetchDependency = (dependency, offset = 0) => {
    const {
      fetch,
      component: { id: componentId, dataBindings = {} },
    } = this.props

    const { getBaseURL, appVersion, getStore } = this.context
    const { offset: prevOffset } = this.state

    if (offset === 0 && prevOffset > 0) {
      offset = prevOffset
    }

    if (dependency.paginate) {
      dependency = { ...dependency, offset, limit: 20 }
    }

    return fetch(getBaseURL(), {
      ...dependency,
      datasource: this.getDatasource(dependency.datasourceId),
      componentId,
      bindingIds: Object.keys(dataBindings),
      appVersion,
      state: getStore().getState(),
      getParams: this.getParams,
    })
  }

  getDatasource = datasourceId => {
    const { getDatasources } = this.context
    const datasources = getDatasources()

    return datasources[datasourceId]
  }

  startPolling = dependency => {
    this.pollTimers.push(
      setInterval(() => {
        this.fetchDependency(dependency)
      }, 1000)
    )
  }

  setup = async () => {
    let { component } = this.props
    component = component || {}

    const prevDependencies = this.getDependencies()

    await this.fetchDependencies(prevDependencies)

    const newDependencies = this.getDependencies().filter(item =>
      prevDependencies.some(
        prevDep =>
          prevDep.id === item.id && prevDep.columnFilter !== item.columnFilter
      )
    )

    if (newDependencies.length > 0) {
      await this.fetchDependencies(newDependencies)
    }

    this._loaded = true

    window.setTimeout(() => {
      this.setState({ loaded: true })
    }, 500)

    const { componentActions, onVisit } = component

    const isInitialMount = !this.previouslyMounted
    this.previouslyMounted = true
    const { inputsDifferent } = this

    if ((isInitialMount || !inputsDifferent) && onVisit && componentActions) {
      const { actionId } = onVisit
      const action = componentActions[actionId]

      await this.executeAction(componentActions[actionId])
      const reloadRequired = hasDataAction(action)

      if (reloadRequired && isInitialMount) {
        this.handleRefresh(false)
      }
    }
  }

  executeAction = async action => {
    if (!action) {
      return
    }

    await executeAction(action, { ...this.context, ...this.getChildContext() })
  }

  teardown = () => {
    this.pollTimers.forEach(timer => {
      clearInterval(timer)
    })

    this.pollTimers = []
  }

  componentDidMount() {
    const { active } = this.props

    if (active) {
      this.setup()
    }

    this.keyboardListener = Keyboard.addListener(
      'keyboardDidShow',
      this.handleResize
    )

    Dimensions.addEventListener('change', this.screenSizeChange)

    window.setTimeout(this.handleResize, 100)
  }

  componentWillUnmount() {
    this.keyboardListener.remove()
    this.teardown()

    Dimensions.removeEventListener('change', this.screenSizeChange)
  }

  screenSizeChange = () => {
    this.forceUpdate()
  }

  getInputsDifferent = (oldValues, newValues) => {
    const { component } = this.props
    const inputIds = getInputDependencies(component)

    const oldDeps = filterObject(oldValues, inputIds)
    const newDeps = filterObject(newValues, inputIds)

    return !shallowEqual(oldDeps, newDeps)
  }

  shouldComponentUpdate(newProps, newState) {
    const { active, component, params, formInputs } = this.props
    const { updateVersion } = this.state

    const inputsDifferent = this.getInputsDifferent(
      formInputs,
      newProps.formInputs
    )

    if (
      newProps.active &&
      (newProps.component !== component ||
        newProps.params !== params ||
        (newProps.active && !active) ||
        inputsDifferent ||
        newState.updateVersion !== updateVersion)
    ) {
      this.inputsDifferent = inputsDifferent
      this.setState({
        offset: 0,
        reachedPaginationEnd: false,
        initialRender: true,
      })
      setTimeout(this.setup, 0)
    }

    if (!newProps.active && active) {
      this.teardown()
    }

    return newProps.active || active
  }

  getStatusBarHidden = () => {
    const { component } = this.props
    const { statusBarStyle } = component

    if (statusBarStyle === statusBarStyles.HIDDEN) {
      return true
    }

    return false
  }

  getBarStyle = () => {
    const { component } = this.props
    const { statusBarStyle } = component

    if (statusBarStyle === statusBarStyles.LIGHT_CONTENT) {
      return 'light-content'
    } else if (statusBarStyle === statusBarStyles.DARK_CONTENT) {
      return 'dark-content'
    }
  }

  handleScroll = e => {
    if (isCloseToBottom(e.nativeEvent)) {
      this.checkPagination()
    }
  }

  checkPagination = async () => {
    const {
      loading,
      reachedPaginationEnd,
      offset: prevOffset,
      initialRender,
    } = this.state

    const offset = initialRender ? prevOffset : prevOffset + 20

    const dependencies = this.getDependencies().filter(dep => {
      const { paginate, limit } = dep
      let belowLimit = true

      if (limit) {
        belowLimit = offset < Number(limit) + 20
      }

      return paginate && belowLimit
    })

    if (loading || reachedPaginationEnd || dependencies.length === 0) {
      return
    }

    this.setState({ offset, loading: true })

    const promises = dependencies.map(dep => this.fetchDependency(dep, offset))

    const results = await Promise.all(promises)

    const positiveResults = results.filter(
      r => r && r.value && r.value.data && r.value.data.length >= 20
    )

    if (positiveResults.length === 0) {
      this.setState({
        reachedPaginationEnd: true,
      })
    }

    setTimeout(() => {
      this.setState({ loading: false, initialRender: false })
    }, 100)
  }

  handleResize = (width, height) => {
    const { loaded } = this.state
    const { component } = this.props

    if (height) {
      this._innerHeight = height

      if (this._loaded && height < this._scrollViewHeight) {
        this.checkPagination()
      }
    }

    if (this._scrollView && component.reverseScroll) {
      window.setTimeout(() => {
        this._scrollView.scrollToEnd({ animated: loaded })
      }, 10)
    }
  }

  handleLayout = ({ nativeEvent }) => {
    this._scrollViewHeight = nativeEvent.layout.height
  }

  scrollViewRef = el => {
    this._scrollView = el
  }

  isListView = () => {
    const { component } = this.props
    const body = component.layout.body[0]

    if (!body) {
      return false
    }

    const bodyChildren = body.children || []
    const listChildren = bodyChildren.filter(obj => isListObject(obj))

    return listChildren.length === 1
  }

  get useFlatList() {
    return this.isListView()
  }

  render() {
    const { active, component, topScreen, layoutGuides } = this.props

    const { loading, refreshing } = this.state
    const { getApp, getAssetURL } = this.context

    const app = getApp()

    let {
      backgroundColor,
      backgroundImage,
      backgroundSize,
      backgroundPositionX,
      backgroundPositionY,
      layout,
    } = component

    const branding = (app && app.branding) || defaultBranding
    backgroundColor = normalizeColor(backgroundColor || '@background', branding)
    backgroundImage = backgroundImage && getAssetURL(backgroundImage)
    backgroundPositionX = backgroundPositionX || 'center'
    backgroundPositionY = backgroundPositionY || 'center'
    backgroundSize = backgroundSize || 'cover'

    const offsetTop = layoutGuides.top - 20
    const offsetBottom = layoutGuides.bottom

    const avoidingBehavior = Platform.OS === 'android' ? undefined : 'padding'

    const paddingStyles = {
      paddingTop: Math.max(0, offsetTop),
      paddingBottom: offsetBottom,
    }

    if (offsetTop < 0 && isMobileWeb()) {
      paddingStyles.top = offsetTop
    }

    const scrollViewInnerStyles = {
      paddingBottom: offsetBottom + 20,
    }

    const backgroundStyles = {
      backgroundColor,
    }

    const heightStyles = {}

    if (Platform.OS !== 'web') {
      heightStyles.height = Dimensions.get('window').height
    }

    return (
      <View style={[styles.root, styles.wrapper, backgroundStyles]}>
        {backgroundImage && (
          <BackgroundImage
            source={backgroundImage}
            resizeMode={backgroundSize}
            positionX={backgroundPositionX}
            positionY={backgroundPositionY}
          />
        )}
        <StatusBar
          translucent
          backgroundColor="rgba(0, 0, 0, 0)"
          barStyle={this.getBarStyle()}
          hidden={this.getStatusBarHidden()}
        />
        <View style={[styles.wrapper]}>
          <KeyboardAvoidingView
            behavior={avoidingBehavior}
            style={styles.wrapper}
          >
            {this.useFlatList ? (
              <ListBody
                body={layout.body}
                style={{ ...paddingStyles, ...scrollViewInnerStyles }}
                component={component}
                active={active}
                topScreen={topScreen}
                onEndReached={this.checkPagination}
                loading={loading}
                onRefresh={this.handleRefresh}
                refreshing={refreshing}
              />
            ) : (
              <ScrollView
                ref={this.scrollViewRef}
                keyboardShouldPersistTaps="handled"
                style={[styles.scrollView]}
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                onContentSizeChange={this.handleResize}
                onLayout={this.handleLayout}
                onScroll={this.handleScroll}
                scrollEventThrottle={200}
              >
                <View style={[paddingStyles, scrollViewInnerStyles]}>
                  <View>
                    {layout.body.map(obj => (
                      <ObjectRenderer
                        key={obj.id}
                        object={obj}
                        component={component}
                        active={active}
                        topScreen={topScreen}
                      />
                    ))}
                  </View>
                </View>
              </ScrollView>
            )}
          </KeyboardAvoidingView>
        </View>
        <View
          style={[styles.fixedContent, paddingStyles, heightStyles]}
          pointerEvents="box-none"
        >
          <KeyboardAvoidingView
            behavior="padding"
            style={[styles.wrapper, styles.offset]}
            pointerEvents="box-none"
            enabled={component.keyboardBehavior === keyboardBehaviors.AVOID}
          >
            {layout.fixed.map(obj => (
              <ObjectRenderer
                key={obj.id}
                object={obj}
                component={component}
                active={active}
                topScreen={topScreen}
              />
            ))}
          </KeyboardAvoidingView>
        </View>
      </View>
    )
  }
}

const styles = StyleSheet.create({
  root: {
    overflow: 'hidden',
  },
  wrapper: {
    flex: 1,
  },
  offset: {
    ...Platform.select({
      web: {
        paddingBottom: SafeAreaInsets.bottom,
      },
    }),
  },
  scrollView: {
    flex: 1,
  },
  fixedContent: {
    zIndex: 1,
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
  },
})

const mapStateToProps = state => ({
  formInputs: getFormInputs(state),
})

export default connect(mapStateToProps, { fetch })(Screen)
